import { cn } from 'cn';
import { type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { requestBugReportOpen, useBugReportWidget } from '~/modules/bugReport';

import { Button, Icon, Tooltip } from '@vestigas/ui';

type P = ComponentStyling;

const arrowIcon = <Icon metaphor="arrowForward" size="sm" />;

// Keeps the tooltip (invisible) arrow off the pill, so the pill stays hoverable to its edge.
const tooltipTargetMargin = { x: 12, y: 20 };

function AlphaBadgeTooltipContent() {
  const { t } = useTranslation('common');
  const bugReportVariant = useBugReportWidget();

  return (
    <div className="flex flex-col items-start gap-2">
      <span>{t(($) => $.alphaBadge.toolTip)}</span>
      {bugReportVariant === 'none' ? null : (
        <Button
          buttonStyle="link"
          className="h-auto min-w-0 p-0 text-sm font-medium text-tiga-action-lighter hover:underline"
          endIcon={arrowIcon}
          text={t(($) => $.alphaBadge.feedbackButton.text)}
          type="button"
          onClick={requestBugReportOpen}
        />
      )}
    </div>
  );
}

// Clicking the pill must not reach the tooltip's close-on-click listener.
function stopClick(event: MouseEvent) {
  event.stopPropagation();
}

const tooltipContent = <AlphaBadgeTooltipContent />;

/**
 * "Alpha" pill with a tooltip that sets expectations for an alpha feature
 * and links to the app's feedback form.
 */
export function AlphaBadge({ className, style }: P) {
  const { t } = useTranslation('common');

  return (
    <Tooltip
      align="start"
      className="pointer-events-auto max-w-md bg-gray-900 py-3 text-left"
      closeOnClick
      content={tooltipContent}
      detect="hover-interact"
      placement="bottom"
      targetMargin={tooltipTargetMargin}
      // Grace period for moving the pointer from the badge into the tooltip.
      timeOut={300}
    >
      <span
        className={cn(
          'cursor-default rounded-full border border-blue-200 bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-tiga-action-default select-none hover:border-blue-300 hover:bg-blue-200',
          className,
        )}
        style={style}
        onClick={stopClick}
      >
        {t(($) => $.alphaBadge.text)}
      </span>
    </Tooltip>
  );
}
